import React, { memo, useCallback, useState } from 'react';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Sheet, SheetContent, SheetTrigger } from 'components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Card, CardContent } from 'components/ui/card';
import { IconSearch } from '@app/icons';
import { RenderFilterProps } from '@app/templates';
import {
  Kato,
  Oked,
  Tender,
  TenderTypes,
  User,
  UserStatus,
  UserStatusNames,
} from '@app/models';
import { useForm } from '../hooks';
import TendersFilter from '../TendersFilter/TendersFilter';
import { VehiclesFilterData } from '../VehiclesFilter';
import { KatoSelectTrigger } from '@app/components';
import { OkedSelectTrigger } from '@app/components';
import { SelectOption } from '@app/components';

interface FormData {
  kato: SelectOption<Kato> | null;
  status: SelectOption<UserStatus> | null;
  oked: SelectOption<Oked> | null;
}

export type UsersFilterData<T> = {
  tenderType: TenderTypes;
} & Partial<T>;

interface Props extends RenderFilterProps<UsersFilterData<Tender | User>> {
  okedParentNumber?: number;
}

function UsersFilter(props: Props) {
  const { onSubmit, pending, okedParentNumber } = props;
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [tenderType, setTenderType] = useState<TenderTypes>(
    TenderTypes.REQUEST
  );
  const { values, onChange, setValues } = useForm<FormData>({
    kato: null,
    status: null,
    oked: null,
  });

  const onChangeTenderType = useCallback(
    (item: TenderTypes) => () => {
      setTenderType(item);

      onSubmit({
        tenderType: item,
      });
    },
    [onSubmit]
  );

  const onSubmitTendersFilter = useCallback(
    (tenderFilterData: Partial<Tender>) => {
      onSubmit({
        tenderType,
        ...tenderFilterData,
      });
    },
    [onSubmit, tenderType]
  );

  const onClickSearch = useCallback(() => {
    const params: VehiclesFilterData<User> = {
      tenderType,
    };

    if (!!values.kato) {
      params.katoCode = values.kato.value.code;
    }

    if (values.oked) {
      params.specializationCode = values.oked.value.code;
    }

    if (!!values.status) {
      params.status = values.status.value;
    }

    onSubmit(params);
  }, [onSubmit, tenderType, values.kato, values.oked, values.status]);

  const onClickClear = useCallback(() => {
    setValues({
      kato: null,
      status: null,
      oked: null,
    });

    onSubmit({
      tenderType,
    });
  }, [onSubmit, setValues, tenderType]);

  const onClickClearKato = useCallback(() => {
    setValues({
      kato: null,
    });
  }, [setValues]);

  const onClickClearOked = useCallback(() => {
    setValues({
      oked: null,
    });
  }, [setValues]);

  const userStatusOptions = [UserStatus.FREE, UserStatus.BUSY].map((item) => ({
    label: UserStatusNames[item],
    value: item.toString(),
  }));

  return (
    <Card className="w-full border-0 shadow-none">
      <CardContent className="p-0">
        <div className="mb-6">
          <RadioGroup
            value={tenderType.toString()}
            onValueChange={(value) =>
              onChangeTenderType(parseInt(value) as TenderTypes)()
            }
            className="flex flex-col gap-4 sm:flex-row sm:gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value={TenderTypes.REQUEST.toString()}
                id="request"
              />
              <Label
                htmlFor="request"
                className="text-sm font-medium cursor-pointer"
              >
                Поиск вакансий
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value={TenderTypes.OFFER.toString()} id="offer" />
              <Label
                htmlFor="offer"
                className="text-sm font-medium cursor-pointer"
              >
                Список специалистов
              </Label>
            </div>
          </RadioGroup>
        </div>

        {tenderType === TenderTypes.REQUEST && (
          <TendersFilter
            pending={pending}
            onSubmit={onSubmitTendersFilter}
            okedParentNumber={okedParentNumber}
          />
        )}

        {tenderType === TenderTypes.OFFER && (
          <>
            <div className="space-y-6 hidden md:block">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 w-full">
                <div className="space-y-2">
                  <OkedSelectTrigger
                    label="СПЕЦИАЛИЗАЦИЯ"
                    name="oked"
                    value={!!values.oked ? values.oked.value.nameRu : ''}
                    placeholder="Выберите категорию"
                    parentNumber={60000001}
                    onChange={(value) => onChange(value, 'oked')}
                    onClickClear={onClickClearOked}
                  />
                </div>

                <div className="space-y-2">
                  <KatoSelectTrigger
                    label="РЕГИОН"
                    value={
                      (!!values.kato ? values.kato.value.nameRu : '') ||
                      undefined
                    }
                    placeholder="Выберите регион"
                    onChange={onChange}
                    name="kato"
                    onClickClear={onClickClearKato}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status" className="text-sm font-medium">
                    СТАТУС
                  </Label>
                  <Select
                    value={values.status?.value?.toString() || ''}
                    onValueChange={(value: string) => {
                      const selectedOption = userStatusOptions.find(
                        (opt) => opt.value === value
                      );
                      onChange(selectedOption || null, 'status');
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Выберите статус" />
                    </SelectTrigger>
                    <SelectContent>
                      {userStatusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex flex-col gap-3 pt-4 border-t w-full sm:flex-row sm:justify-between">
                <Button
                  type="button"
                  onClick={onClickClear}
                  className="reset-btn w-full sm:w-auto"
                >
                  Сбросить все
                </Button>

                <Button
                  disabled={pending}
                  onClick={onClickSearch}
                  className="w-full sm:w-auto min-w-[120px]"
                >
                  {pending ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                  ) : (
                    <IconSearch className="h-4 w-4 mr-2" />
                  )}
                  Найти
                </Button>
              </div>
            </div>

            <div className="md:hidden">
              <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="w-full">
                    <IconSearch className="h-4 w-4 mr-2" />
                    Расширенный поиск
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-[80vh]">
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <OkedSelectTrigger
                          label="СПЕЦИАЛИЗАЦИЯ"
                          name="oked"
                          value={!!values.oked ? values.oked.value.nameRu : ''}
                          placeholder="Выберите категорию"
                          parentNumber={60000001}
                          onChange={(value) => onChange(value, 'oked')}
                          onClickClear={onClickClearOked}
                        />
                      </div>

                      <div className="space-y-2">
                        <KatoSelectTrigger
                          label="РЕГИОН"
                          value={
                            (!!values.kato ? values.kato.value.nameRu : '') ||
                            undefined
                          }
                          placeholder="Выберите регион"
                          onChange={onChange}
                          name="kato"
                          onClickClear={onClickClearKato}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="status-mobile"
                          className="text-sm font-medium"
                        >
                          СТАТУС
                        </Label>
                        <Select
                          value={values.status?.value?.toString() || ''}
                          onValueChange={(value: string) => {
                            const selectedOption = userStatusOptions.find(
                              (opt) => opt.value === value
                            );
                            onChange(selectedOption || null, 'status');
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Выберите статус" />
                          </SelectTrigger>
                          <SelectContent>
                            {userStatusOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 pt-4 border-t w-full">
                      <Button
                        type="button"
                        onClick={onClickClear}
                        variant="outline"
                        className="w-full"
                      >
                        Сбросить все
                      </Button>

                      <Button
                        disabled={pending}
                        onClick={() => {
                          onClickSearch();
                          setIsSheetOpen(false);
                        }}
                        className="w-full"
                      >
                        {pending ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                        ) : (
                          <IconSearch className="h-4 w-4 mr-2" />
                        )}
                        Найти
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default memo(UsersFilter);
